import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Share2, Check as CheckIcon, Trash2 } from "lucide-react";

/**
 * Share access dialog. Extracted from AppLayout.js. Manages grantees on a
 * given scope (project / list / etc.) via POST /shares + DELETE /shares/{id}.
 *
 * Props:
 *   scope:        { kind, id, name }
 *   users:        full users list (for name/email lookup and picker pool)
 *   scopeKindMap: map from UI kind → backend kind
 *   onClose:      () => void
 */
export default function ShareDialog({ scope, users, scopeKindMap, onClose }) {
  const backendKind = scopeKindMap[scope.kind] || scope.kind;
  const [existing, setExisting] = useState([]);
  const [pickerId, setPickerId] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/shares?scope_kind=${backendKind}&scope_id=${scope.id}`);
      setExisting(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const grantedIds = new Set(existing.map((s) => s.grantee_id));
  // Grantee candidates: everyone except admins and people who already have access
  const candidates = users.filter((u) => u.role !== "admin" && u.role !== "super_admin" && !grantedIds.has(u.id));

  const share = async () => {
    if (!pickerId) return;
    try {
      await api.post("/shares", { scope_kind: backendKind, scope_id: scope.id, grantee_id: pickerId });
      toast.success("Access granted");
      setPickerId("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/shares/${id}`);
      toast.success("Access revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share access to {scope.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            People you share this with can view and edit all tasks under this scope. Their filters and views stay independent of yours.
          </p>
          <div className="flex gap-2">
            <Select value={pickerId} onValueChange={setPickerId}>
              <SelectTrigger className="flex-1" data-testid="share-user-picker">
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                {candidates.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No more members to invite</div>}
                {candidates.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} <span className="text-xs text-slate-400 ml-1">· {u.role.replace("_", " ")}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={share} disabled={!pickerId} className="bg-purple-600 hover:bg-purple-700" data-testid="share-grant-btn">
              <Share2 className="h-3.5 w-3.5 mr-1" /> Share
            </Button>
          </div>
          {existing.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">People with access</div>
              <div className="space-y-1 max-h-52 overflow-y-auto scrollbar-thin">
                {existing.map((s) => {
                  const u = users.find((x) => x.id === s.grantee_id);
                  if (!u) return null;
                  return (
                    <div key={s.id} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-slate-50" data-testid={`share-entry-${s.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckIcon className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-slate-900 truncate">{u.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => revoke(s.id)}
                        className="p-1 text-slate-400 hover:text-red-600 rounded"
                        data-testid={`share-revoke-${s.id}`}
                        title="Revoke access"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
